import { client, urlFor, getSanityEditLink, resolveInternalLink } from "./sanity-client";
import initScrollAnimations from "./animations";

async function fetchTechnologyPage(slug) {
    try {
        const query = `*[_type == "technologyPage" && slug.current == $slug][0]{
            ...,
            showHero,
            showSections,
            showCaseStudies,
            sections[]{
                ...,
                _key,
                features[]{
                    ...,
                    _key
                }
            },
            caseStudies[]{
                ...,
                _key
            }
        }`;
        const params = { slug };
        const data = await client.fetch(query, params);
        return data;
    } catch (error) {
        console.error('Error fetching technology page data:', error);
        return null;
    }
}


function renderSections(sections, containerId, docId) {
    const container = document.getElementById(containerId);
    if (!container || !sections) return;

    let html = '';

    sections.forEach((section, index) => {
        // Alternate specific styles if needed, or use a generic one. 
        // The original HTML had two slightly different layouts (one with grey background).
        // Let's alternate background for visual separation.
        const isAlternate = index % 2 !== 0; // 2nd section (index 1) was alternate in original
        const bgStyle = isAlternate ? 'style="background-color: #f5f5f6;"' : '';
        const paddingTop = isAlternate ? 'pt-8' : '';

        // Generate Features HTML
        let featuresHtml = '';
        if (section.features) {
            const featuresPath = section._key ? `sections[_key=="${section._key}"].features` : `sections[${index}].features`;
            const featuresEditLink = docId ? getSanityEditLink(docId, 'technologyPage', featuresPath, 'Edit Features') : '';

            featuresHtml += `<section class="pt-md-10 position-relative">`;
            section.features.forEach((feature, fIndex) => {
                const featurePath = feature._key ? `${featuresPath}[_key=="${feature._key}"]` : `${featuresPath}[${fIndex}]`;
                const singleFeatureEditLink = docId ? getSanityEditLink(docId, 'technologyPage', featurePath, 'Edit Feature') : '';

                featuresHtml += `
                    <div class="number-content-2 position-relative" style="padding-top: 2rem;">
                        ${singleFeatureEditLink}
                        <div class="number-container col-1 gap-1"><span>0${fIndex + 1}</span></div>
                        <div class="product-text-continer mt-3 col-md-10 px-sm-2 px-0">
                            <div class="px-0">
                                <h3 class="product-text-container-heading tech-section-sider-value-head2 text-black mb-4">
                                    ${feature.title}
                                </h3>
                                <p class="product-text-container-description fs-lg text-gray-700 mb-sm-6 mb-0">
                                    ${feature.description}
                                </p>
                                ${feature.caption ? `
                                <div class="mb-3">
                                    <span class="badge rounded-pill bg-success-soft">
                                        <span class="h6 text-uppercase">${feature.caption}</span>
                                    </span>
                                </div>` : ''}
                                ${feature.points && feature.points.length > 0 ? `
                                <div class="row">
                                    ${feature.points.map(point => `
                                    <div class="col-12 col-md-6 text-gray-700 mb-3">
                                        <div class="d-flex">
                                            <div class="badge badge-rounded-circle bg-primary mt-1 me-4">
                                                <i class="fe fe-check"></i>
                                            </div>
                                            <p class="mb-0">${point}</p>
                                        </div>
                                    </div>
                                    `).join('')}
                                </div>
                                ` : ''}
                            </div>
                        </div>
                    </div>
                 `;
            });
            featuresHtml += '</section>';
        }

        html += `
        <section class="${paddingTop} section-v3" ${bgStyle} data-animate="fade-up">
            <div class="container px-md-0">
                <div class="row justify-content-between gx-0 mx-md-0">
                    <div class="col-12 col-md-4 position-md-sticky d-flex align-items-center h-md-75 mx-0" style="top: 0;">
                        <!-- Sidebar -->
                        <aside class="flex-grow-1 py-sm-8 py-4 pt-6 py-md-10">
                            <div class="row px-0 mb-md-0 position-relative">
                                ${docId ? getSanityEditLink(docId, 'technologyPage', section._key ? `sections[_key=="${section._key}"]` : `sections[${index}]`, 'Edit Section') : ''}
                                <div class="">
                                    <!-- Heading -->
                                    <h1 class="fw-bold technology-section-head2">
                                        ${section.title}
                                    </h1>
                                </div>
                            </div>
                            <!-- Text -->
                            <p class="fs-lg mb-sm-7 mb-0 text-muted">
                                ${section.description}
                            </p>
                        </aside>
                    </div>
                    <div class="col-12 col-md-7 product-details-container">
                        ${featuresHtml}
                    </div>
                </div>
            </div>
        </section>
        `;
    });


    container.innerHTML = html;
}

function renderCaseStudies(caseStudies, containerId, pageTitle, docId) {
    const container = document.getElementById(containerId);
    if (!container || !caseStudies) return;

    const caseStudiesSection = container.closest('section');
    if (caseStudiesSection && docId && !caseStudiesSection.querySelector('.sanity-edit-btn')) {
        const editLink = getSanityEditLink(docId, 'technologyPage', 'caseStudies', 'Edit Case Studies');
        if (editLink) {
            caseStudiesSection.classList.add('position-relative');
            caseStudiesSection.insertAdjacentHTML('afterbegin', editLink);
        }
    }

    let html = '';
    const colClass = caseStudies.length === 2 ? 'col-12 col-md-6 col-lg-6' : 'col-12 col-md-6 col-lg-4';

    caseStudies.forEach(study => {
        let imageUrl = '';
        if (study.image) {
            imageUrl = urlFor(study.image).url();
        } else {
            // Basic fallback logic for demo purposes if no image in Sanity
            // Using title keywords to guess image
            if (study.title.toLowerCase().includes('ransomware')) imageUrl = '/assets/img/case-study/ransomeware-cs.webp';
            else if (study.title.toLowerCase().includes('encryption')) imageUrl = '/assets/img/case-study/encryption-cs.webp';
            else if (study.title.toLowerCase().includes('cloud')) imageUrl = '/assets/img/case-study/cloud-migration-cs.webp';
            else if (study.title.toLowerCase().includes('data protection')) imageUrl = '/assets/img/case-study/dataprotection-cloud-cs.webp';
            else if (study.title.toLowerCase().includes('infrastructure')) imageUrl = '/assets/img/case-study/infra-cs.webp';
            else if (study.title.toLowerCase().includes('cross platform')) imageUrl = '/assets/img/case-study/cross-plateform-cs.webp';
            else if (study.title.toLowerCase().includes('vidiqulus')) imageUrl = '/assets/img/photos/project-vidiQulus.webp';
            else if (study.title.toLowerCase().includes('squash')) imageUrl = '/assets/img/case-study/squash-cs.webp';
            else if (study.title.toLowerCase().includes('nlp')) imageUrl = '/assets/img/case-study/cross-platform-cs.webp';
            else if (study.title.toLowerCase().includes('saas')) imageUrl = '/assets/img/case-study/startuo-cs-qa-cycle.webp';
            else imageUrl = '/assets/img/case-study/infra-cs.webp';
        }

        // Handle generic or modal link
        let cardAttributes = '';
        if (study.modalTarget) {
            cardAttributes = `data-bs-toggle="modal" data-bs-target="${study.modalTarget}" style="cursor: pointer;"`;
        } else if (study.link) {
            const finalLink = resolveInternalLink(study.link);
            cardAttributes = `onclick="window.location.href = '${finalLink}'" style="cursor: pointer;"`;
        }

        // Video icon logic
        let videoIconHtml = '';
        if (study.modalTarget) {
            videoIconHtml = `
            <div class="icon icon-sm text-white">
                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M0 0h24v24H0z"/><circle fill="#335EEA" opacity=".3" cx="12" cy="12" r="9"/><path d="M11.158 15.873l3.993-3.482a.5.5 0 000-.753L11.16 8.134a.5.5 0 00-.83.376v6.986a.5.5 0 00.83.377z" fill="#335EEA"/></g></svg>
            </div>`;
        }

        html += `
            <div class="${colClass}">
                <div class="card br-12 mb-6 overlay overlay-black overlay-60 bg-cover shadow-light-lg" 
                    style="background-image: url(${imageUrl}); max-height: 350px; min-height: 350px; background-position:0 50%;"
                    ${cardAttributes}>
                    <a class="card-body mt-auto position-relative">
                        <h3 class="text-white">
                            ${study.title}
                        </h3>
                    </a>
                    <a class="card-meta pb-4">
                        <hr class="card-meta-divider bg-white-20 mb-4" style="opacity: 0.2;">
                        <p class="h6 text-uppercase text-white-80 text-opacity-80 mb-0 me-auto">
                            ${pageTitle}
                        </p>
                         ${videoIconHtml}
                    </a>
                </div>
            </div>
        `;
    });

    container.innerHTML = html;
}

document.addEventListener('DOMContentLoaded', async () => {
    const techContainer = document.querySelector('[data-tech-slug]');
    if (!techContainer) return;

    const slug = techContainer.getAttribute('data-tech-slug');
    if (!slug) return;

    const data = await fetchTechnologyPage(slug);

    if (data) {
        // Hero toggle
        if (data.showHero === false) {
            const heroSection = document.getElementById('tech-hero-section');
            if (heroSection) heroSection.style.display = 'none';
        } else {
            if (data.title) {
                const titleEl = document.getElementById('tech-page-title');
                if (titleEl) {
                    titleEl.textContent = data.title;
                    document.title = `${data.title} | Coriolis Technologies`;

                    const titleContainer = titleEl.parentElement;
                    if (titleContainer && !titleContainer.querySelector('.sanity-edit-btn')) {
                        const editLink = getSanityEditLink(data._id, 'technologyPage', 'title', 'Edit Page Title');
                        if (editLink) {
                            titleContainer.classList.add('position-relative');
                            titleContainer.insertAdjacentHTML('afterbegin', editLink);
                        }
                    }
                }
            }
            if (data.heroDescription) {
                const descEl = document.getElementById('tech-page-description');
                if (descEl) descEl.textContent = data.heroDescription;
            }
            if (data.headerImage) {
                const imageUrl = urlFor(data.headerImage).url();
                const heroSection = document.getElementById('tech-hero-section');
                if (heroSection) {
                    heroSection.style.backgroundImage = `url(${imageUrl})`;
                }
            }
        }

        // Sections toggle
        if (data.showSections === false) {
            const sectionsEl = document.getElementById('technology-sections-container');
            if (sectionsEl) sectionsEl.style.display = 'none';
        } else if (data.sections) {
            renderSections(data.sections, 'technology-sections-container', data._id);
        }

        // Case Studies toggle
        if (data.showCaseStudies === false) {
            const csContainer = document.getElementById('case-studies-container');
            const csSection = csContainer ? csContainer.closest('section') : document.getElementById('case-studies-section');
            if (csSection) csSection.style.display = 'none';
        } else if (data.caseStudies) {
            renderCaseStudies(data.caseStudies, 'case-studies-container', data.title || 'Expertise', data._id);
        }

        // Re-initialize animations after content injection
        initScrollAnimations();

        // Navbar transparency logic
        handleNavbarScroll();
        window.addEventListener('scroll', handleNavbarScroll);
    }
});

function handleNavbarScroll() {
    const navbar = document.querySelector('.navbar');
    if (!navbar) return;

    if (window.scrollY > 50) {
        navbar.classList.add('navbar-v3-scrolled');
        navbar.classList.remove('navbar-v3-transparent');
    } else {
        navbar.classList.remove('navbar-v3-scrolled');
        navbar.classList.add('navbar-v3-transparent');
    }
}
